import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RecordedAction, RecordedStep, Step, GestureMode, FingerMode } from '../../Models/recorded-action-model';
import { MobileSessionService, AppType } from '../../Services/mobile-session.service';
import { MobileDevice } from '../apk-upload/apk-upload.component';
import { DeviceScreenComponent } from '../device-screen/device-screen.component';
import { GestureControlsComponent } from '../gesture-controls/gesture-controls.component';
import { RestService } from '../../Services/rest.service';
import { Literals } from '../../literals';

@Component({
  selector: 'ze-mobile-live-view',
  templateUrl: './mobile-live-view.component.html',
  styleUrls: ['./mobile-live-view.component.scss']
})
export class MobileLiveViewComponent implements OnInit, OnDestroy {

  @ViewChild(DeviceScreenComponent,    { static: false }) deviceScreen!: DeviceScreenComponent;
  @ViewChild(GestureControlsComponent, { static: false }) gestureControls!: GestureControlsComponent;

  // ── Global state ──────────────────────────────────────────────────────────────
  step: Step = 'upload';
  errorMessage: string | null = null;
  uploadedAppName: string | null = null;
  isUploading = false;
  isCreatingSession = false;

  // ── App type (Native vs Flutter) ──────────────────────────────────────────────
  /** Bound to the toggle in the upload panel. Propagated to session service before createSession(). */
  appType: AppType = 'native';

  // ── Gesture state (shared with device screen & controls) ──────────────────────
  fingerMode: FingerMode    = '1';
  selectedMode: GestureMode = Literals.TAP;

  // ── Recording state ───────────────────────────────────────────────────────────
  isRecording       = false;
  isPaused          = false;
  isModalOpen       = false;
  isFetchingLocator = false;
  currentStepName   = '';

  /** When true, uninstall + DELETE are in progress — UI shows a spinner on the button. */
  isEndingSession = false;

  /** When true, the installed APK is uninstalled from the device when the session ends. */
  uninstallOnEnd = true;

  /**
   * All recorded steps. Each step has a name and its own actions list.
   * These are PRESERVED across sessions — only cleared by the user explicitly.
   */
  recordedSteps: RecordedStep[] = [];

  constructor(
    // Exposed as public so the template can read session.activeDriver for the badge
    public session: MobileSessionService,
    private ngZone: NgZone,
    private restService: RestService
  ) {}

  private readonly STEPS_STORAGE_KEY = 'ze_recorded_steps';

  ngOnInit() {
    // Restore recorded steps from localStorage
    try {
      const saved = localStorage.getItem(this.STEPS_STORAGE_KEY);
      if (saved) {
        this.recordedSteps = JSON.parse(saved);
      }
    } catch { this.recordedSteps = []; }

    // Restore active session from sessionStorage (handles page refresh)
    if (this.session.restoreSessionFromStorage()) {
      this.appType      = this.session.appType;
      this.step         = 'streaming';
      this.isRecording  = false;
      this.isPaused     = false;
      console.log('🔄 Session restored from storage:', this.session.sessionId);
      // Re-connect WebSocket after view is initialised
      setTimeout(() => {
        if (this.deviceScreen) {
          this.deviceScreen.connectWebSocket();
        }
      }, 300);
    }
  }

  ngOnDestroy() {
    this.deviceScreen.closeSocket();
    this.updateDeviceStatus('free');
    this.session.clearSessionFromStorage();
    this.session.deleteSession(this.uninstallOnEnd);
  }

  // ── Device status ─────────────────────────────────────────────────────────────

  /**
   * Calls PUT /mobileDevices/updateStatus/{deviceId}/{status}
   * where status is 'busy' (session starting) or 'free' (session ending).
   * Fire-and-forget — errors are non-fatal.
   */
  private updateDeviceStatus(status: 'busy' | 'free'): void {
    if (!this.session.selectedDevice) return;
    const deviceId = this.session.selectedDevice._id;
    if (!deviceId) return;
    // Don't mark free if session was never successfully created
    if (status === 'free' && !this.session.sessionId) return;
    this.restService.put('updateStatus', {}, deviceId, status)
      .subscribe({
        next: () => console.log(`${status}`),
        error: (err: any) => console.warn(`⚠️ updateStatus(${status}) failed`, err)
      });
  }

  // ── App type toggle ───────────────────────────────────────────────────────────

  onAppTypeChanged(type: AppType) {
    this.appType         = type;
    this.session.appType = type;
    // console.log(`🔀 App type switched to: ${type}`);
  }

  // ── Device selection ──────────────────────────────────────────────────────────

  onDeviceSelected(device: MobileDevice | null) {
    this.session.selectedDevice = device;
  }

  // ── Persist steps to localStorage ────────────────────────────────────────────

  private saveSteps(): void {
    try {
      localStorage.setItem(this.STEPS_STORAGE_KEY, JSON.stringify(this.recordedSteps));
    } catch {}
  }

  // ── Convenience getter for the currently active step's actions ────────────────

  get currentStepActions(): RecordedAction[] {
    if (!this.currentStepName) return [];
    const step = this.recordedSteps.find(s => s.name === this.currentStepName);
    return step ? step.actions : [];
  }

  // ── Upload events ─────────────────────────────────────────────────────────────

  onUploadComplete(appName: string) {
    this.uploadedAppName = appName;
    this.startSession();
  }

  onUploadError(msg: string) {
    this.setError(msg);
  }

  onErrorDismissed() {
    this.errorMessage = null;
    this.step = 'upload';
  }

  // ── Session ───────────────────────────────────────────────────────────────────

  private async startSession() {
    this.isCreatingSession = true;
    this.step = 'connecting';
    // Propagate selected app type to service before session creation
    this.session.appType = this.appType;
    try {
      await this.session.createSession(this.uploadedAppName!);
      // Mark the device as busy in the backend
      this.updateDeviceStatus('busy');
      // Persist session so a page refresh restores the stream
      this.session.saveSessionToStorage();
      this.deviceScreen.connectWebSocket();
    } catch (err) {
      this.setError((err as any).message || 'Session creation failed');
    } finally {
      this.isCreatingSession = false;
    }
  }

  // ── Device screen events ──────────────────────────────────────────────────────

  onStepChange(step: Step)   { this.step = step; }
  onStreamError(msg: string) { this.setError(msg); }

  onActionCaptured(action: RecordedAction) {
    if (!this.isRecording || this.isPaused) return;
    if (!this.currentStepName) return;

    // ── Auto-assign ##variableN for get-text actions ──────────────────────────
    if (action.action === Literals.GET_TEXT) {
      // Count ALL get-text actions across ALL steps to get the next variable number
      const totalGetText = this.recordedSteps
        .reduce((count, s) => count + s.actions.filter(a => a.action === Literals.GET_TEXT).length, 0);
      action = { ...action, value: `##variable${totalGetText + 1}` };
    }

    const stepIdx = this.recordedSteps.findIndex(s => s.name === this.currentStepName);
    if (stepIdx < 0) return;

    const actions = this.recordedSteps[stepIdx].actions;
    const existingIdx = actions.findIndex(a => a.id === action.id);

    const updatedSteps = [...this.recordedSteps];
    if (existingIdx >= 0) {
      updatedSteps[stepIdx] = {
        ...updatedSteps[stepIdx],
        actions: actions.map(a => a.id === action.id ? action : a)
      };
    } else {
      updatedSteps[stepIdx] = {
        ...updatedSteps[stepIdx],
        actions: [...actions, action]
      };
    }
    this.recordedSteps = updatedSteps;
    this.saveSteps();
  }

  onLocatorFetchingChange(fetching: boolean) { this.isFetchingLocator = fetching; }

  onContextMenuAction(event: { action: 'get-text' | 'get-value'; x: number; y: number; result: string }) {
    // console.log(`[Inspect] ${event.action} at (${event.x}, ${event.y}): ${event.result}`);
  }

  /** Relay the type request from device-screen → gesture-controls panel */
  onTypeRequested(event: { coords: { x: number; y: number; realX: number; realY: number }; element: any }) {
    this.gestureControls.openTypePanel(event);
  }

  /** User confirmed text in gesture-controls panel — send it and record with pre-tap element */
  onTypeConfirmed(event: { coords: { x: number; y: number; realX: number; realY: number }; element: any; text: string }) {
    this.session.sendType(event.text);
    if (this.isRecording && !this.isPaused) {
      this.deviceScreen.recordTypeAction(event.coords, event.text, event.element);
    }
  }

  onClearAndTypeConfirmed(event: { coords: { x: number; y: number; realX: number; realY: number }; element: any; text: string }) {
    this.session.sendClearAndType(event.text);
    if (this.isRecording && !this.isPaused) {
      this.deviceScreen.recordTypeAction(event.coords, event.text, event.element);
    }
  }

  // ── Gesture controls events ───────────────────────────────────────────────────

  onFingerModeChanged(mode: FingerMode)  { this.fingerMode = mode; }
  onModeChanged(mode: GestureMode)       { this.selectedMode = mode; }

  onRecordingStarted(stepName: string) {
    const exists = this.recordedSteps.find(s => s.name === stepName);
    if (!exists) {
      this.recordedSteps = [...this.recordedSteps, { name: stepName, actions: [], collapsed: false }];
    }
    this.currentStepName = stepName;
    this.deviceScreen.resetActionCounter();
    this.isRecording = true;
    this.isPaused    = false;
    this.saveSteps();
  }

  onRecordingResumed(stepName: string) {
    const exists = this.recordedSteps.find(s => s.name === stepName);
    if (!exists) {
      this.recordedSteps = [...this.recordedSteps, { name: stepName, actions: [], collapsed: false }];
    }
    this.currentStepName = stepName;
    this.isRecording     = true;
    this.isPaused        = false;
  }

  onRecordingPaused() {
    this.isPaused = true;
  }

  onRecordingStopped() {
    this.isRecording = false;
    this.isPaused    = false;
  }

  onNewStepStarted(stepName: string) {
    this.recordedSteps = [...this.recordedSteps, { name: stepName, actions: [], collapsed: false }];
    this.currentStepName = stepName;
    this.deviceScreen.resetActionCounter();
    this.isRecording = true;
    this.isPaused    = false;
    this.saveSteps();
  }

  // ── Recorded actions events ───────────────────────────────────────────────────

  onStepsCleared() {
    this.recordedSteps   = [];
    this.currentStepName = '';
    this.saveSteps();
  }

  onStepRemoved(stepName: string) {
    this.recordedSteps = this.recordedSteps.filter(s => s.name !== stepName);
    if (this.currentStepName === stepName) this.currentStepName = '';
    this.saveSteps();
  }

  onStepRenamed(event: { oldName: string; newName: string }) {
    const idx = this.recordedSteps.findIndex(s => s.name === event.oldName);
    if (idx < 0) return;
    const updatedSteps = [...this.recordedSteps];
    updatedSteps[idx]  = { ...updatedSteps[idx], name: event.newName };
    this.recordedSteps = updatedSteps;
    if (this.currentStepName === event.oldName) {
      this.currentStepName = event.newName;
    }
    this.saveSteps();
  }

  onStepCleared(stepName: string) {
    const idx = this.recordedSteps.findIndex(s => s.name === stepName);
    if (idx < 0) return;
    const updated = [...this.recordedSteps];
    updated[idx] = { ...updated[idx], actions: [] };
    this.recordedSteps = updated;
    // If current step was cleared, keep it selected but reset action counter
    if (this.currentStepName === stepName && this.deviceScreen) {
      this.deviceScreen.resetActionCounter();
    }
    this.saveSteps();
  }

  onActionRemoved(event: { stepName: string; actionId: number }) {
    const idx = this.recordedSteps.findIndex(s => s.name === event.stepName);
    if (idx < 0) return;
    const updatedSteps = [...this.recordedSteps];
    updatedSteps[idx]  = {
      ...updatedSteps[idx],
      actions: updatedSteps[idx].actions.filter(a => a.id !== event.actionId)
    };
    this.recordedSteps = updatedSteps;
    this.saveSteps();
  }

  // ── End session ───────────────────────────────────────────────────────────────

  async reset() {
    if (this.isEndingSession) return;
    this.isEndingSession = true;

    this.deviceScreen.closeSocket();

    // Mark device as free before deleting the session
    this.updateDeviceStatus('free');

    await this.session.deleteSession(this.uninstallOnEnd);

    // Clear persisted session so a refresh starts fresh
    this.session.clearSessionFromStorage();
    this.session.reset();
    this.step             = 'upload';
    this.errorMessage     = null;
    this.uploadedAppName  = null;
    this.isRecording      = false;
    this.isPaused         = false;
    this.isModalOpen      = false;
    this.isEndingSession  = false;
    // appType intentionally kept — user likely wants to re-upload same type
    // ⚠️ NOTE: recordedSteps are intentionally NOT cleared here.
  }

  private setError(msg: string) {
    this.errorMessage = msg;
    this.step = 'error';
    console.error(msg);
  }
  
  async onVerifyElement(action: RecordedAction) {
    if (!this.deviceScreen) return;
    try {
      const xml = await this.session.fetchPageSource();
      this.deviceScreen.highlightElement(action, xml);
    } catch {
      this.deviceScreen.highlightElement(action, null);
    }
  }
  
}